import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFormSyncErrors } from 'redux-form';
import get from 'lodash/get';
import pick from 'lodash/pick';
import set from 'lodash/set';
import { formatNumber, validatePhoneNumberLength } from 'libphonenumber-js';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import { useBoolState } from 'nallian-shared-ui/lib/hooks';
import { isValidEmail, generateErrorsForRequiredFields } from 'nallian-shared-ui/lib/lib/validate';
import { GetRights } from 'nallian-shared-ui/lib/selectors';
import { hasSomeRights } from 'nallian-shared-ui/lib/lib/routesHelper';
import { Modal, Mo, Button } from 'nallian-shared-ui/lib/components';
import PrincipalDetailsTab from './details/PrincipalDetailsTab';
import PrincpalRolesTab from './roles/PrincipalRolesTab';
import { Step, StepGroup, StepNumber, DisplayNone } from '../../../../portal/app/styles';
import { PRINCIPAL_FORM_NAME } from '../../constants';

const STEPS = { ONE: 'ONE', TWO: 'TWO' };

const PRINCIPAL_RIGHTS = [
  'PRINCIPALS.MANAGE.ALL',
  'PRINCIPALS.MANAGE.ALL.EXCEPTSYSTEMORGS',
  'PRINCIPALS.MANAGE.ORGANISATION',
];

const FIRST_TAB_REQUIRED_FIELDS = ['fullName', 'timeZone', 'languageId'];
const FIRST_TAB_CONDITIONAL_REQUIRED_FIELDS = ['primaryEmailAddress', 'primaryPhoneNumber'];
const SECOND_TAB_REQUIRED_FIELDS = [];
const THIRD_TAB_REQUIRED_FIELDS = [];
const REQUIRED_FIELDS = [
  ...FIRST_TAB_REQUIRED_FIELDS,
  ...SECOND_TAB_REQUIRED_FIELDS,
  ...THIRD_TAB_REQUIRED_FIELDS,
];

const validateEmailError = email => !email || email.length < 0 || !isValidEmail(email);

const validatePhoneNumberError = (phone = '') => {
  // returns to long, to short or undefined
  return validatePhoneNumberLength(phone) || !formatNumber(phone, 'E.164');
};

const validate = (values, { t }) => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);

  if (
    validateEmailError(values.primaryEmailAddress) &&
    validatePhoneNumberError(values.primaryPhoneNumber)
  ) {
    errors.primaryEmailAddress = t('emailOrPhoneNumberRequiredError');
    errors.primaryPhoneNumber = t('emailOrPhoneNumberRequiredError');
  } else {
    if (
      validatePhoneNumberError(values.primaryPhoneNumber) &&
      validateEmailError(values.primaryEmailAddress)
    ) {
      errors.primaryEmailAddress = t('primaryEmailAddressEmptyErr');
    }

    if (
      validateEmailError(values.primaryEmailAddress) &&
      validatePhoneNumberError(values.primaryPhoneNumber)
    ) {
      errors.primaryPhoneNumber = t('primaryPhoneNumberEmptyErr');
    }

    if (values?.inviteRequest?.sendInvitation && validateEmailError(values.primaryEmailAddress)) {
      errors.primaryEmailAddress = t('primaryEmailAddressEmptyErr');
    }
  }

  if (values?.communityApps?.length > 0) {
    values.communityApps.forEach((ca, index) => {
      if (ca?.features?.length > 0) {
        ca.features.forEach((f, fIndex) => {
          if (!f.roles || f?.roles?.length <= 0) {
            set(
              errors,
              `communityApps[${index}].features[${fIndex}].roles`,
              t('pleaseSelectRoles')
            );
          }
        });
      }
    });
  }

  return errors;
};

const PrincipalForm = ({
  submitProcessing,
  dataLoading,
  data,
  returnRoute,
  onSubmit,
  header,
  editMode,
  organisationId,
  prefillLocalFields,
  hideInviteFields,
  forbidManageBecauseSystemOrg,
  showPrincipalIdField,
  principal,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const redirectTab = new URLSearchParams(location.search).get('redirectTab');

  const [extraConfirmation, setExtraConfirmation, unsetExtraConfirmation] = useBoolState(false);
  const [step, setStep] = useState(redirectTab === 'identities' ? STEPS.THREE : STEPS.ONE);

  const handleSetStep = useCallback((_, item) => setStep(item.step), [setStep]);
  const handleGoBack = useCallback(() => navigate(returnRoute), [returnRoute, navigate]);

  const handlePreviousStep = useCallback(() => {
    const keys = Object.keys(STEPS);
    const nextIndex = keys.indexOf(step) - 1;
    setStep(keys[nextIndex]);
  }, [step, setStep]);

  const handleNextStep = useCallback(() => {
    const keys = Object.keys(STEPS);
    const nextIndex = keys.indexOf(step) + 1;
    setStep(keys[nextIndex]);
  }, [step, setStep]);

  const rights = useSelector(GetRights);
  const errors = useSelector(state => getFormSyncErrors(PRINCIPAL_FORM_NAME)(state));
  const submitFailed = useSelector(state => get(state, `form.${PRINCIPAL_FORM_NAME}.submitFailed`));
  const formValues = useSelector(state => get(state, `form.${PRINCIPAL_FORM_NAME}.values`));

  const showConfirmation = useMemo(() => {
    if (editMode) return false;

    const roles = get(formValues, 'communityApps[0].features[0].roles.length', 0);
    const userGroups = get(formValues, 'userGroups.length', 0);

    return roles <= 0 && userGroups <= 0;
  }, [formValues, editMode]);

  const handleSubmitOnConfirmation = useCallback(() => onSubmit(formValues), [
    onSubmit,
    formValues,
  ]);

  const readOnly = useMemo(
    () => forbidManageBecauseSystemOrg || !hasSomeRights(rights, PRINCIPAL_RIGHTS),
    [rights, forbidManageBecauseSystemOrg]
  );

  const firstTabsErrors = useMemo(
    () =>
      submitFailed &&
      Object.keys(
        pick(errors, [...FIRST_TAB_REQUIRED_FIELDS, ...FIRST_TAB_CONDITIONAL_REQUIRED_FIELDS])
      ).length > 0,
    [errors, submitFailed]
  );

  const secondTabsErrors = useMemo(
    () => submitFailed && Object.keys(pick(errors, SECOND_TAB_REQUIRED_FIELDS)).length > 0,
    [errors, submitFailed]
  );

  const isLastStep = step === STEPS.TWO;
  const isFirstStep = step === STEPS.ONE;

  return (
    <>
      <FormModal
        closeIcon
        header={header}
        dataLoading={dataLoading}
        submitProcessing={submitProcessing}
        customOnSubmit={!isLastStep ? handleNextStep : undefined}
        onSubmit={showConfirmation ? setExtraConfirmation : onSubmit}
        data={data}
        t={t}
        validate={validate}
        form={PRINCIPAL_FORM_NAME}
        cancelRoute={returnRoute}
        noSaveButton={readOnly}
        onDimmerClick={handleGoBack}
        onCancel={!isFirstStep ? handlePreviousStep : handleGoBack}
        cancelButtonContent={!isFirstStep ? t('back') : t(readOnly ? 'close' : 'cancel')}
        saveButtonIconPosition={isLastStep ? 'left' : 'right'}
        saveButtonIcon={isLastStep ? 'save' : 'arrow right'}
        saveButtonContent={!isLastStep ? t('next') : t('save')}
      >
        <StepGroup fluid>
          <Step
            fluid
            link
            active={step === STEPS.ONE}
            step={STEPS.ONE}
            onClick={handleSetStep}
            width="calc(100% / 3)"
            error={firstTabsErrors}
          >
            <StepNumber>1</StepNumber>
            <Step.Content>
              <Step.Title>{t('user')}</Step.Title>
              <Step.Description>{t('userDetails')}</Step.Description>
            </Step.Content>
          </Step>
          <Step
            fluid
            link
            active={step === STEPS.TWO}
            step={STEPS.TWO}
            onClick={handleSetStep}
            width="calc(100% / 3)"
            error={secondTabsErrors}
          >
            <StepNumber>2</StepNumber>
            <Step.Content>
              <Step.Title>{t('roles')}</Step.Title>
              <Step.Description>{t('appsAndRoles')}</Step.Description>
            </Step.Content>
          </Step>
        </StepGroup>
        <DisplayNone active={step !== STEPS.ONE}>
          <PrincipalDetailsTab
            readOnly={readOnly}
            editMode={editMode}
            organisationId={organisationId}
            data={data}
            hideInviteFields={hideInviteFields}
            prefillLocalFields={prefillLocalFields}
            showPrincipalIdField={showPrincipalIdField}
          />
        </DisplayNone>
        <DisplayNone active={step !== STEPS.TWO}>
          <PrincpalRolesTab
            editMode={editMode}
            readOnly={readOnly}
            organisationId={organisationId}
            principal={principal}
          />
        </DisplayNone>
      </FormModal>
      <Modal open={extraConfirmation} basic size="tiny">
        <Mo.Header>{t('areYouSureContinueWithoutUserHasRoles')}</Mo.Header>
        <Mo.Actions>
          <Button onClick={unsetExtraConfirmation} inverted content={t('no')} />
          <Button
            primary
            inverted
            color="green"
            onClick={handleSubmitOnConfirmation}
            loading={submitProcessing}
            disabled={submitProcessing}
            content={t('yes')}
          />
        </Mo.Actions>
      </Modal>
    </>
  );
};

PrincipalForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  dataLoading: PropTypes.bool,
  submitProcessing: PropTypes.bool,
  header: PropTypes.string.isRequired,
  returnRoute: PropTypes.string.isRequired,
  forbidManageBecauseSystemOrg: PropTypes.bool.isRequired,
  isSystemOrg: PropTypes.bool.isRequired,
  organisationId: PropTypes.string,
  prefillLocalFields: PropTypes.bool,
  hideInviteFields: PropTypes.bool,
  showPrincipalIdField: PropTypes.bool,
  data: PropTypes.object,
  editMode: PropTypes.bool,
  principal: PropTypes.object,
};

PrincipalForm.defaultProps = {
  dataLoading: false,
  submitProcessing: false,
  data: {},
  editMode: false,
  organisationId: undefined,
  hideInviteFields: false,
  prefillLocalFields: false,
  showPrincipalIdField: false,
  principal: undefined,
};

export default PrincipalForm;
